import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';

Modal.setAppElement('#root');

const Categories = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newCategory, setNewCategory] = useState({ name: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFetchingCategories, setIsFetchingCategories] = useState(true);

  const categoriesCollectionRef = collection(db, "categories");

  // ==================== REAL-TIME LISTENER ====================
  useEffect(() => {
    if (!user?.uid) return;

    setIsFetchingCategories(true);

    const q = query(categoriesCollectionRef);

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const categoryList = snapshot.docs.map((doc) => ({ 
          ...doc.data(), 
          id: doc.id 
        }));
        setCategories(categoryList);
        setIsFetchingCategories(false);
      },
      (err) => {
        console.error("Error in categories listener:", err);
        setError("Failed to load categories");
        setShowModal(true);
        setIsFetchingCategories(false);
      }
    );

    // Cleanup listener when component unmounts
    return () => unsubscribe();
  }, [user?.uid]);

  // Search filter
  useEffect(() => {
    const filtered = categories.filter((category) =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCategories(filtered);
  }, [searchTerm, categories]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCategory({ ...newCategory, [name]: value });
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      setError("Category name cannot be empty");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(categoriesCollectionRef, {
        ...newCategory,
        createdAt: Timestamp.now()
      });

      setSuccessMessage("Category added successfully");
      setNewCategory({ name: '' });
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add category");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateCategory = async () => {
    setUpdate(true);
    try {
      const categoryDoc = doc(db, "categories", editingCategory.id);
      await updateDoc(categoryDoc, newCategory);

      setSuccessMessage("Category updated successfully");
      setEditingCategory(null);
      setNewCategory({ name: '' });
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update category");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleEditCategory = (categoryToEdit) => {
    setEditingCategory(categoryToEdit);
    setNewCategory({ name: categoryToEdit.name });
    setIsEditModalOpen(true);
  };

  const confirmDeleteCategory = (categoryToDelete) => {
    setCurrentCategory(categoryToDelete);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteCategory = async (id) => {
    setUpdate(true);
    try {
      const categoryDoc = doc(db, "categories", id);
      await deleteDoc(categoryDoc);

      setSuccessMessage("Category deleted successfully");
    } catch (error) {
      setError("Failed to delete category");
    } finally {
      setUpdate(false);
      setIsDeleteModalOpen(false);
      setShowModal(true);
    }
  };

  if (loading) return <Loader text={"LOADING"} />;
  if (!userData) return <Loader text={"READING"} />;

  const allowedRoles = ['Cashier', 'Admin', 'Supreme'];

  const displayContent = () => {
    if (isFetchingCategories) {
      return <p className="loading-text">Loading categories ...</p>;
    }

    if (filteredCategories.length === 0) {
      return searchTerm 
        ? <p className="loading-text">No categories match your search.</p>
        : <p className="loading-text">No categories found. Add your first category.</p>;
    }

    return filteredCategories.map((categoryItem) => (
      <div key={categoryItem.id} className="class-item">
        <div className="row gap-2 align-center">
          <p>{categoryItem.name}</p>
        </div>
        <div className="row align-center justify-center gap-15">
          <button className="list-btn" onClick={() => handleEditCategory(categoryItem)}>
            <i className="fas fa-edit"></i>
          </button>
          <button className="list-btn delete-btn" onClick={() => confirmDeleteCategory(categoryItem)}>
            <i className="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>
    ));
  };

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Manage Categories</h2>
            <Link 
              to="#" 
              className="home-link" 
              onClick={() => window.location.reload()}
            >
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">            
            <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Category</button>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Category Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Category"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Category</h2>
        <form className="session-form">
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Category Name e.g. Electronics"
            value={newCategory.name}
            onChange={handleInputChange}
          />
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddCategory}>Add Category</button>
          </div>
        </form>
      </Modal>

      {/* Edit Category Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          setNewCategory({ name: '' });
          setEditingCategory(null);
        }}
        contentLabel="Edit Category"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Category</h2>
        <form className="session-form">
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Category Name e.g. Electronics"
            value={newCategory.name}
            onChange={handleInputChange}
          />
          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
                setIsEditModalOpen(false);
                setNewCategory({ name: '' });
                setEditingCategory(null);
              }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateCategory}>Update Category</button>
          </div>
        </form>
      </Modal>

      {/* Delete Category Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        contentLabel="Delete Category"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Category</h2>
        {currentCategory && (
          <div>
            <p>Are you sure you want to delete <strong>{currentCategory.name}</strong>?</p>
            <p>All products in this category will need to be reassigned.</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" type="button" onClick={() => handleDeleteCategory(currentCategory.id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Categories;